import { Typography } from "@mui/material"
import { ReactElement } from "react"
import { Control, FieldValues, UseFormWatch } from "react-hook-form"

import { TypeTestStandard } from "../../../../../constants/ttr/registerDevice"
import {
    getFullyTestedRegisterName,
    getPartiallyTestedRegisterName,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import AppTooltip from "../../../../helper/appTooltip"
import ValidatedSwitch from "../../../../inputs/validated/validatedSwitch"
import { AppCard, AppCardContent } from "../../../../surfaces/appCard"

interface TypeTestProps {
    details: TypeTestStandard
    name: string
    control: Control<FieldValues, unknown>
    watch: UseFormWatch<FieldValues>
}

/** Component that allows a user to enter the test status for a given standard.
 * Provides a tooltip for any disabled switches
 */
const TypeTest = ({
    details,
    name,
    control,
    watch,
}: TypeTestProps): ReactElement => {
    const fullyTestedRegisterName = getFullyTestedRegisterName(name)
    const partiallyTestedRegisterName = getPartiallyTestedRegisterName(name)

    const isFullyTested: boolean = watch(fullyTestedRegisterName) ?? false
    const isPartiallyTested: boolean =
        watch(partiallyTestedRegisterName) ?? false

    const bothTypesTested = isFullyTested && isPartiallyTested

    const allTypeTestsAvailable =
        details.canBeFullyTested && details.canBePartiallyTested

    let message = ""

    if (!details.canBeFullyTested && details.canBePartiallyTested) {
        message = `${details.displayName} cannot be fully tested`
    }

    if (!details.canBePartiallyTested && details.canBeFullyTested) {
        message = `${details.displayName} cannot be partially tested`
    }

    if (!details.canBeFullyTested && !details.canBePartiallyTested) {
        message = `${details.displayName} cannot be fully or partially tested`
    }

    if (allTypeTestsAvailable && (isFullyTested || isPartiallyTested)) {
        message = `${details.displayName} can only be fully or partially tested`
    }

    const fullyTestedIsRestrictedValue =
        !details.canBeFullyTested && isFullyTested
    const partiallyTestedIsRestrictedValue =
        !details.canBePartiallyTested && isPartiallyTested

    const validateFullyTestedSwitch = (): boolean | string => {
        if (bothTypesTested) {
            return "The same standard cannot both be fully and partially tested"
        }

        if (fullyTestedIsRestrictedValue) {
            return "This standard cannot be fully tested"
        }

        return true
    }

    const validatePartiallyTestedSwitch = (): boolean | string => {
        if (bothTypesTested) {
            return "The same standard cannot both be fully and partially tested"
        }

        if (partiallyTestedIsRestrictedValue) {
            return "This standard cannot be partially tested"
        }

        return true
    }

    return (
        <AppTooltip title={message}>
            <AppCard>
                <AppCardContent>
                    <Typography variant="h6" fontWeight={800}>
                        {details.displayName}
                    </Typography>
                </AppCardContent>
                <ValidatedSwitch
                    registerName={fullyTestedRegisterName}
                    label="Fully Type Tested"
                    control={control}
                    labelPlacement="start"
                    spaceBetweenLabel
                    disabled={isPartiallyTested || !details.canBeFullyTested}
                    customRules={{
                        validate: validateFullyTestedSwitch,
                    }}
                />
                <ValidatedSwitch
                    registerName={partiallyTestedRegisterName}
                    label="Partially Type Tested"
                    control={control}
                    labelPlacement="start"
                    spaceBetweenLabel
                    disabled={isFullyTested || !details.canBePartiallyTested}
                    customRules={{
                        validate: validatePartiallyTestedSwitch,
                    }}
                />
            </AppCard>
        </AppTooltip>
    )
}

export default TypeTest
